import $ from 'jquery';
import ko from 'knockout';
import _ from 'underscore';
import { ResponsiveSearchAd } from '@bingads-webui-campaign/campaign-ui-schemas';
import dynamicTextExtension from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/dynamicTextExtension';
import positionExtension from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/positionExtension';
import KoSchema from '@bingads-webui-component-legacy/ko-schema/index';
import parsedTextMaxLengthExtension from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/parsedTextMaxLengthExtension';
import { getPreviewList } from '@bingads-webui-campaign/summary-grid-helper';
import config from '@bingads-webui-component-legacy/config/index';
import instrumentation from '@bingads-webui-static-legacy/instrumentation/index';
import constants from '@bingads-webui-static-legacy/instrumentation/constants';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');
const i18nErrors = window.globalLegacyI18n.get('component/i18n/loader!component/api-errors-campaign');

const headlinePositionFields = ['Headline1', 'Headline2', 'Headline3', 'None'];
const descriptionPositionFields = ['Description1', 'Description2', 'None'];

const headlineErrorMap = {
  lessMinRequire: i18nErrors.get('Error_Campaign_API_ResponsiveSearchAdsHeadlinesLessThanMinRequired'),
  duplicate: i18nErrors.get('Error_Campaign_API_ResponsiveSearchAdsDuplicateHeadlines'),
  lessPosition: i18nErrors.get('Error_Campaign_API_ResponsiveSearchAdNotEnoughHeadlinesForPositions'),
};

const descriptionErrorMap = {
  lessMinRequire: i18nErrors.get('Error_Campaign_API_ResponsiveSearchAdsDescriptionsLessThanMinRequired'),
  duplicate: i18nErrors.get('Error_Campaign_API_ResponsiveSearchAdsDescriptionsDuplication'),
  lessPosition: i18nErrors.get('Error_Campaign_API_ResponsiveSearchAdNotEnoughDescriptionsForPositions'),
};

// Doesn't validate when init the view.
// When user's input pass the validation or submit the view, do validation.
function validate(model, miniRequire, fields, positionFields, errorMap) {
  let error;
  const list = _.chain(fields)
    .filter((field) => !_.isEmpty(model[field]().trim()))
    .map((field) => ({
      Text: model[field]().trim(),
      PinnedField: model[`${field}Position`](),
    })).value();

  if (_.size(list) < miniRequire) {
    error = errorMap.lessMinRequire;
  } else if (_.size(_.uniq(list, 'Text')) < _.size(list)) {
    error = errorMap.duplicate;
  } else if (_.size(getPreviewList(list, positionFields)) < miniRequire) {
    error = errorMap.lessPosition;
  }
  if (_.isEmpty(error)) {
    model.needValication(true);
  }
  return model.needValication() ? error : '';
}

export class HeadlinesViewModel {
  constructor({
    defaultCount,
    maxCount,
    model = null,
    miniRequire,
    isForVNext = false,
    activityLogger,
  } = {}) {
    const headlineFields = config.get('headlineFields');

    this.activityLogger = activityLogger;
    this.model = model;
    this.positionFieldMap = (key) => _.result({ 'None': isForVNext ? i18n.get('Ad_Pin') : '', 'Headline1': '1', 'Headline2': '2', 'Headline3': '3' }, key);
    _.each(headlineFields, (key) => {
      ResponsiveSearchAd.properties[key] = _.clone(ResponsiveSearchAd.properties.Headline);
    });
    KoSchema.properties(this, ResponsiveSearchAd, headlineFields);
    dynamicTextExtension.extend(this, headlineFields);
    parsedTextMaxLengthExtension.extend(this, headlineFields, 'ResponsiveSearchAd');
    positionExtension.extendHeadlines(this);

    const initHeadlines = _.result(this, 'model');

    _.each(headlineFields, (field, index) => {
      this[`${field}Position`] = ko.observable(
        _.chain(initHeadlines).result(index).result('PinnedField', 'None').value()
      );
      this[`${field}IsAIGeneratedAsset`] = ko.observable(
        _.chain(initHeadlines).result(index).result('IsAlgoGeneratedAsset', false).value()
      );
      const fieldText = _.chain(initHeadlines).result(index).result('Asset').result('Text', '').value();

      this[field](_.isNull(fieldText) ? '' : fieldText);
      const logChange = _.once(() => {
        // Clean up AI-generated asset light bulb when content is changed.
        const aiGeneratedAssetContainer = $(`.${field}-AIGeneratedAsset`);

        if (!_.isEmpty(aiGeneratedAssetContainer) && aiGeneratedAssetContainer.length > 0) {
          _.first(aiGeneratedAssetContainer).style.display = 'none';
        }

        if (this.activityLogger) {
          this.activityLogger.signal(`ad field change: ${field}`, 'responsive search ad editor');
        } else {
          instrumentation.logMessage(
            [constants.LOGCATEGORY.SUBSTRATE],
            constants.LOGLEVEL.INFO,
            'responsive search ad editor',
            `ad field change: ${field}`
          );
        }
      });

      this[field].subscribe(() => {
        logChange();
      }, this);
    });

    this.showHeadlineCount = ko.observable(Math.max(defaultCount, _.size(initHeadlines)));
    this.maxCount = maxCount;

    this.needValication = ko.observable(false);
    this.errorMessage = ko.computed(() => {
      return validate(this, miniRequire, headlineFields, headlinePositionFields, headlineErrorMap);
    });
  }
}

export class DescriptionsViewModel {
  constructor({
    model = null,
    defaultCount,
    maxCount,
    miniRequire,
    isForVNext = false,
    activityLogger,
  } = {}) {
    const descriptionFields = config.get('descriptionFields');

    this.activityLogger = activityLogger;
    this.model = model;
    this.positionFieldMap = (key) => _.result({ 'None': isForVNext ? i18n.get('Ad_Pin') : '', 'Description1': '1', 'Description2': '2' }, key);
    _.each(descriptionFields, (key) => {
      ResponsiveSearchAd.properties[key] = _.clone(ResponsiveSearchAd.properties.Description);
    });

    KoSchema.properties(this, ResponsiveSearchAd, descriptionFields);
    dynamicTextExtension.extend(this, descriptionFields);
    parsedTextMaxLengthExtension.extend(this, descriptionFields, 'ResponsiveSearchAd');
    positionExtension.extendDescriptions(this);

    const initDescriptions = _.result(this, 'model');

    _.each(descriptionFields, (field, index) => {
      this[`${field}Position`] = ko.observable(
        _.chain(initDescriptions).result(index).result('PinnedField', 'None').value()
      );
      this[`${field}IsAIGeneratedAsset`] = ko.observable(
        _.chain(initDescriptions).result(index).result('IsAlgoGeneratedAsset', false).value()
      );
      const fieldText = _.chain(initDescriptions).result(index).result('Asset').result('Text', '').value();

      this[field](_.isNull(fieldText) ? '' : fieldText);
      const logChange = _.once(() => {
        // Clean up AI-generated asset light bulb when content is changed.
        const aiGeneratedAssetContainer = $(`.${field}-AIGeneratedAsset`);

        if (!_.isEmpty(aiGeneratedAssetContainer) && aiGeneratedAssetContainer.length > 0) {
          _.first(aiGeneratedAssetContainer).style.display = 'none';
        }

        if (this.activityLogger) {
          this.activityLogger.signal(`ad field change: ${field}`, 'responsive search ad editor');
        } else {
          instrumentation.logMessage(
            [constants.LOGCATEGORY.SUBSTRATE],
            constants.LOGLEVEL.INFO,
            'responsive search ad editor',
            `ad field change: ${field}`
          );
        }
      });

      this[field].subscribe(() => {
        logChange();
      }, this);
    });

    this.showDescriptionCount = ko.observable(Math.max(defaultCount, _.size(initDescriptions)));
    this.maxCount = maxCount;

    this.needValication = ko.observable(false);
    this.errorMessage = ko.computed(() => {
      return validate(this, miniRequire, descriptionFields, descriptionPositionFields, descriptionErrorMap);
    });
  }
}
